import { Button, LinearProgress, Typography } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import type React from 'react';
import { Card } from 'src/Administration/Common';
import type { UseReindexReturn } from 'src/hooks/useReindex';

interface ReindexCardProps {
  reindex: UseReindexReturn;
  classes: Record<string, string>;
}

export default function ReindexCard({ reindex, classes }: ReindexCardProps): React.ReactElement {
  const phaseLabel = reindex.swapPhaseLabel;
  // Only the reindexing phase has determinate progress; every other phase is
  // a short activity (usually <5s) where a percentage would be misleading.
  const hasDeterminateProgress =
    reindex.total > 0 && (reindex.swapPhase === null || reindex.swapPhase === 'reindexing');

  const percent =
    reindex.total > 0 && hasDeterminateProgress
      ? Math.min(100, Math.round((reindex.completed / reindex.total) * 100))
      : null;

  const statusLine = (() => {
    if (reindex.phase === 'starting') return 'Starting reindex...';
    if (reindex.swapPhase === 'reindexing' && reindex.total > 0) {
      return `Reindexing... ${reindex.completed}/${reindex.total} items`;
    }
    if (phaseLabel) return `${phaseLabel}...`;
    return reindex.total > 0 ? `Reindexing... ${reindex.completed}/${reindex.total} items` : 'Reindexing...';
  })();

  return (
    <Card style={{ marginTop: '20px' }} display="flex">
      <div className={classes.cardTitle}>Search Index</div>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, px: 1 }}>
        Rebuild the search index from the database. Required after bulk data imports or when search results appear
        stale.
      </Typography>
      {reindex.phase === 'running' || reindex.phase === 'starting' ? (
        <Box sx={{ px: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }} aria-live="polite">
            <Typography variant="body2">{statusLine}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {percent !== null ? `${percent}%` : ''}
            </Typography>
          </Box>
          <LinearProgress variant={hasDeterminateProgress ? 'determinate' : 'indeterminate'} value={percent ?? 0} />
        </Box>
      ) : (
        <>
          <Button
            variant="contained"
            color="primary"
            className={classes.btn}
            onClick={() => reindex.handleReindex()}
            disabled={reindex.mutationLoading}
          >
            {reindex.mutationLoading ? 'Starting...' : 'Rebuild Index'}
          </Button>
          {reindex.phase === 'failed' && (
            <Alert
              severity="error"
              sx={{ mt: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => reindex.handleReindex()}
                  disabled={reindex.mutationLoading}
                >
                  Retry
                </Button>
              }
            >
              {reindex.message}
            </Alert>
          )}
          {reindex.phase === 'disconnected' && (
            <Alert
              severity="warning"
              sx={{ mt: 2 }}
              action={
                <Button color="inherit" size="small" onClick={() => reindex.retry()}>
                  Check Status
                </Button>
              }
            >
              {reindex.message}
            </Alert>
          )}
          {reindex.phase === 'idle' && reindex.message && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {reindex.message}
            </Alert>
          )}
        </>
      )}
    </Card>
  );
}
